import { useCallback, useMemo, useRef, type MutableRefObject } from 'react';
import type { Control, Resolver, ResolverOptions } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import {
  reservationFormSchema,
  type ReservationFormInput,
  type ReservationFormValues,
} from '../schema/reservationForm.schema';
import {
  withServerFieldErrors,
  type ServerFieldErrorMap,
} from '../utils/withServerFieldErrors';

export type ReservationFormResolver = Resolver<
  ReservationFormInput,
  unknown,
  ReservationFormValues
>;

/**
 * The reservation form's resolver (Zod schema + persisted server errors) and
 * the ref the `/validate` applier writes the active server errors into.
 *
 * Usage in a dialog:
 *   const { resolver, serverFieldErrorsRef, bindControl, resetServerFieldErrors } =
 *     useReservationFormResolver();
 *   const form = useForm({ resolver, ... });
 *   bindControl(form.control);
 *   useTripLegValidationFormApplier(form, ..., serverFieldErrorsRef);
 *   // and next to every form.reset(...):
 *   resetServerFieldErrors();
 *
 * `bindControl` is needed because the resolver is created before `useForm`
 * returns, yet it has to know the mounted-field set to tell a full-form pass
 * (`handleSubmit`, `trigger()`) from a partial one (blur, `trigger(name)`) —
 * see `withServerFieldErrors`.
 *
 * `resetServerFieldErrors` must accompany every `form.reset(...)`: the dialogs
 * stay mounted across open/close cycles, and `form.reset` clears RHF's error
 * map but knows nothing about this one. Left populated, the first blur of the
 * next session would merge the previous session's server error back onto the
 * new leg/passenger before any fresh `/validate` result arrives. The applier
 * also derives its diff-clear paths from this map, so one reset covers both.
 */
export function useReservationFormResolver(): {
  resolver: ReservationFormResolver;
  serverFieldErrorsRef: MutableRefObject<ServerFieldErrorMap>;
  bindControl: (control: Control<ReservationFormInput>) => void;
  resetServerFieldErrors: () => void;
} {
  const serverFieldErrorsRef = useRef<ServerFieldErrorMap>({});
  const controlRef = useRef<Control<ReservationFormInput> | null>(null);

  const resolver = useMemo<ReservationFormResolver>(
    () =>
      withServerFieldErrors<
        ReservationFormInput,
        unknown,
        ReservationFormValues
      >(
        zodResolver(reservationFormSchema),
        () => serverFieldErrorsRef.current,
        (options: ResolverOptions<ReservationFormInput>) => {
          // react-hook-form validates the whole mounted-field set on
          // handleSubmit / trigger() / its isValid computation, and only the
          // requested names on blur / trigger(name). Without a bound control
          // assume "full" so we never merge where we can't tell.
          const mounted = controlRef.current?._names.mount;
          if (!options.names || !mounted) return true;
          return options.names.length >= mounted.size;
        },
      ),
    [],
  );

  const bindControl = useCallback((control: Control<ReservationFormInput>) => {
    controlRef.current = control;
  }, []);

  const resetServerFieldErrors = useCallback(() => {
    serverFieldErrorsRef.current = {};
  }, []);

  return {
    resolver,
    serverFieldErrorsRef,
    bindControl,
    resetServerFieldErrors,
  };
}
